/**
 * Charm Optical — Premium Legal Pages Enhancement
 *
 * Transforms plain Shopify policy pages (/policies/*) into
 * the premium card-based layout with TOC, numbered sections,
 * and back-to-top button.
 *
 * For /pages/terms-of-service, the template already has the
 * markup — this script only adds interactivity (TOC highlight,
 * back-to-top).
 */
(function () {
  'use strict';

  var PAGE_CONFIG = {
    '/policies/privacy-policy': {
      icon: 'ri-shield-check-line',
      title: 'Privacy Policy',
      subtitle: 'How we collect, use, and protect your personal information.',
      lastUpdated: 'March 11, 2026',
      /**
       * Because the Shopify policy editor produces unstructured HTML,
       * we provide the structured content directly here. This ensures
       * a premium, consistent experience regardless of the raw HTML.
       */
      sections: [
        {
          title: 'Our Commitment to Privacy',
          content: '<p>Charm Optical takes your privacy very seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website at <strong>charmoptical.ca</strong>, use our online booking platform at <strong>see.charmoptical.ca</strong>, or interact with our in-store services.</p><p>We comply with Alberta\'s <em>Personal Information Protection Act</em> (PIPA) and Canada\'s <em>Personal Information Protection and Electronic Documents Act</em> (PIPEDA).</p>'
        },
        {
          title: 'Information We Collect',
          content: '<p>We may collect the following types of information:</p><ul><li><strong>Personal identification:</strong> Name, email address, phone number, and mailing address when you book an appointment, make a purchase, or contact us.</li><li><strong>Health information:</strong> Eye examination records, prescriptions, and medical history as required for optometric care. This is maintained by the attending optometrist under the <em>Health Information Act</em> (HIA) of Alberta.</li><li><strong>Payment information:</strong> Credit card or debit card details processed securely through our payment provider. We do not store full card numbers.</li><li><strong>Insurance details:</strong> Insurance provider, policy number, and coverage information to assist with claims submission.</li><li><strong>Website usage data:</strong> Pages visited, time spent, browser type, and device information collected through analytics tools.</li></ul>'
        },
        {
          title: 'How We Use Your Information',
          content: '<ul><li>To provide and manage your eye care appointments and optical products</li><li>To process transactions and send purchase confirmations</li><li>To send appointment reminders via email and/or SMS</li><li>To assist with insurance claims submission on your behalf</li><li>To improve our website, services, and customer experience</li><li>To communicate important updates about our services</li><li>To comply with legal obligations and professional regulations</li></ul>'
        },
        {
          title: 'Analytics & Cookies',
          content: '<p>Charm Optical uses industry-standard analytics tools, including Google Analytics, to understand how visitors use our website. These tools collect anonymous data such as:</p><ul><li>Pages visited and time spent on each page</li><li>Referring websites and search terms</li><li>Browser type, device, and operating system</li><li>General geographic location (city level)</li></ul><p>This information does not identify you personally. We use cookies — small text files stored on your device — to improve your browsing experience and remember your preferences. You can disable cookies in your browser settings, though some website features may not function optimally.</p>'
        },
        {
          title: 'How We Protect Your Information',
          content: '<p>We implement appropriate technical and organizational measures to protect your personal information, including:</p><ul><li>Secure, encrypted connections (SSL/TLS) for all data transmission</li><li>Restricted access to personal data on a need-to-know basis</li><li>Regular security reviews of our systems and processes</li><li>Secure storage of health records in compliance with Alberta regulations</li></ul><p>While no method of electronic storage is 100% secure, we strive to use commercially acceptable means to protect your personal information.</p>'
        },
        {
          title: 'Information Sharing & Disclosure',
          content: '<p>We will <strong>never</strong> sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:</p><ul><li><strong>With your consent:</strong> When you authorize us to share information with your insurance provider or another health care practitioner.</li><li><strong>Service providers:</strong> Trusted third-party services that assist us in operating our website, conducting business, or servicing you (e.g., payment processors, email services), bound by confidentiality agreements.</li><li><strong>Legal requirements:</strong> When required by law, regulation, or legal process.</li><li><strong>Health care referrals:</strong> When referring you to a specialist or other health care provider for your continued care.</li></ul>'
        },
        {
          title: 'Your Rights',
          content: '<p>Under Alberta\'s PIPA, you have the right to:</p><ul><li><strong>Access</strong> the personal information we hold about you</li><li><strong>Request corrections</strong> to inaccurate or incomplete information</li><li><strong>Withdraw consent</strong> for the collection, use, or disclosure of your information (subject to legal and contractual restrictions)</li><li><strong>File a complaint</strong> with the Office of the Information and Privacy Commissioner of Alberta if you believe your rights have been violated</li></ul><p>To exercise any of these rights, please contact us using the information below.</p>'
        },
        {
          title: 'Email Communications',
          content: '<p>If you subscribe to our mailing list, we may send you occasional updates about promotions, new products, or eye care tips. You can unsubscribe at any time by clicking the "unsubscribe" link in any email or by contacting us directly.</p><p>We will <strong>never</strong> give away or sell your email address to any third party.</p>'
        },
        {
          title: 'Changes to This Policy',
          content: '<p>We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. Updates will be posted on this page with a revised "Last updated" date. We encourage you to review this policy periodically.</p>'
        },
        {
          title: 'Contact Us',
          content: '<p>If you have questions or concerns about this Privacy Policy or our data practices, please contact us:</p><ul class="co-policy__contact-card"><li><strong>Charm Optical</strong></li><li>5035 Ellerslie Road SW, Edmonton, AB T6X 1X2</li><li>Phone: <a href="tel:+17804900090">(780) 490-0090</a></li><li>Email: <a href="mailto:info@charmoptical.ca">info@charmoptical.ca</a></li></ul>'
        }
      ]
    },
    '/policies/refund-policy': {
      icon: 'ri-refund-2-line',
      title: 'Refund & Return Policy',
      subtitle: 'Our guidelines for returns, exchanges, and refunds.',
      lastUpdated: 'June 9, 2026',
      sections: [
        {
          groupBefore: { variant: 'instore', kicker: 'The Following Section Applies To', title: 'In-Store Purchases', subtitle: 'Returns, refunds, and fees for items bought at our Edmonton store.' },
          title: 'Glasses Returns',
          content: '<p>Returning glasses purchased in-store is simple — just call or email us within <strong>30 days of purchase</strong> and we\'ll take care of it.</p>'
        },
        {
          title: 'Contact Lenses & Eye Care Products',
          content: '<p>For health and safety reasons, we can only accept contact lenses and eye care products if the packaging is <strong>completely sealed and unopened</strong>. Opened items cannot be returned.</p><p>Sealed, unopened items must be returned within <strong>30 days of purchase</strong>.</p>'
        },
        {
          title: 'Additional Fees',
          content: '<p>Please be aware that certain returns may be subject to additional charges:</p><ul><li>A <strong>restocking fee</strong> may apply depending on the product and condition of the return.</li><li>If your original purchase was processed through <strong>insurance or direct billing</strong>, an administration fee may be applied to cover the cost of reprocessing the claim.</li></ul><p>Our team will always communicate any applicable fees with you <strong>before</strong> processing your return.</p>'
        },
        {
          groupBefore: { variant: 'online', kicker: 'The Following Section Applies To', title: 'Online Orders Only', subtitle: 'Returns, refunds, insurance, and fees for purchases made through our website.' },
          title: 'Returns',
          content: '<p>If you purchased online and want to return your order, you must do so within <strong>30 days of purchase</strong>:</p><ul><li>Returns are <strong>at your expense</strong>. We recommend a tracked, insured shipping method.</li><li>Email us at <a href="mailto:info@charmoptical.ca">info@charmoptical.ca</a> with your tracking number so we can follow the parcel.</li><li>You must return <strong>everything that came with the original order</strong> — frame, case, cloth, paperwork, and any accessories.</li></ul><p>A <strong>restocking fee</strong> applies to returns, based on what you bought:</p><ul><li><strong>25%</strong> — smart glasses (Ray-Ban Meta, Oakley Meta)</li><li><strong>20%</strong> — prescription or customized glasses &amp; sunglasses</li><li><strong>10%</strong> — non-prescription items</li></ul><p>Items must come back complete and in original condition.</p>'
        },
        {
          title: 'Refunds',
          content: '<p>Once we receive the return and confirm it\'s in original condition, we\'ll refund the amount you paid <strong>less the applicable restocking fee</strong> (see above). Return shipping is non-refundable.</p>'
        },
        {
          title: 'Insurance',
          content: '<p>We\'re not able to process insurance claims or adjustments for online orders. Once the order ships, our part of the service is complete.</p><p>If you want to use your insurance, we strongly recommend purchasing <strong>in-store</strong>. For local orders, we go above and beyond with direct billing and claim support.</p><p>Insurance providers apply much stricter rules to online sales — and while some online retailers do advertise insurance processing, it typically comes with significant limitations.</p>'
        },
        {
          title: 'Contact Us',
          content: '<p>For any questions, please contact us with your name and order number:</p><ul class="co-policy__contact-card"><li><strong>Charm Optical</strong></li><li>5035 Ellerslie Road SW, Edmonton, AB T6X 1X2</li><li>Phone: <a href="tel:+17804900090">(780) 490-0090</a></li><li>Email: <a href="mailto:info@charmoptical.ca">info@charmoptical.ca</a></li></ul>'
        }
      ]
    }
  };

  var path = window.location.pathname.replace(/\/$/, '');

  // ---- Determine page type ----
  var isSystemPolicy = PAGE_CONFIG.hasOwnProperty(path);
  var isTermsPage = path === '/pages/terms-of-service';
  var isLegalPage = isSystemPolicy || isTermsPage;

  if (!isLegalPage) return;

  // ---- Wait for DOM ----
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    if (isSystemPolicy) {
      buildPolicyPage();
    }
    // For all legal pages: add interactivity
    setupTocHighlight();
    setupBackToTop();
    setupSmoothScroll();
  }

  /**
   * Build the premium policy page from our structured data,
   * replacing the plain Shopify content.
   */
  function buildPolicyPage() {
    var config = PAGE_CONFIG[path];
    var sections = config.sections;

    // Find the container where Shopify renders the policy
    var container = document.querySelector('.text-dark.static .container.break-word');
    if (!container) return;

    // Build the premium HTML
    var html = '';

    // Structured data
    html += '<script type="application/ld+json">';
    html += JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": window.location.origin + "/" },
        { "@type": "ListItem", "position": 2, "name": config.title, "item": window.location.href }
      ]
    });
    html += '<\/script>';

    html += '<div class="co-policy">';

    // Breadcrumb
    html += '<div class="co-policy__breadcrumb">';
    html += '<a href="/">Home</a>';
    html += '<span class="co-policy__breadcrumb-sep"><i class="ri-arrow-right-s-line"></i></span>';
    html += '<span>' + config.title + '</span>';
    html += '</div>';

    // Hero
    html += '<div class="co-policy__hero">';
    html += '<div class="co-policy__icon-badge"><i class="' + config.icon + '"></i></div>';
    html += '<h1 class="co-policy__title">' + config.title + '</h1>';
    html += '<p class="co-policy__subtitle">' + config.subtitle + '</p>';
    html += '<span class="co-policy__updated"><i class="ri-calendar-line"></i> Last updated: ' + config.lastUpdated + '</span>';
    html += '</div>';

    // Layout grid
    html += '<div class="co-policy__layout">';

    // TOC sidebar
    html += '<nav class="co-policy__toc" aria-label="Table of contents">';
    html += '<div class="co-policy__toc-title">Contents</div>';
    html += '<ol class="co-policy__toc-list">';
    for (var i = 0; i < sections.length; i++) {
      var id = 'policy-section-' + (i + 1);
      html += '<li><a href="#' + id + '">';
      html += '<span class="co-policy__toc-num">' + (i + 1) + '</span>';
      html += '<span>' + sections[i].title + '</span>';
      html += '</a></li>';
    }
    html += '</ol>';
    html += '</nav>';

    // Content sections
    html += '<div class="co-policy__content">';
    for (var j = 0; j < sections.length; j++) {
      // Optional group divider banner above this section
      if (sections[j].groupBefore) {
        var gb = sections[j].groupBefore;
        var variantClass = 'co-policy__group-divider--' + (gb.variant || 'instore');
        html += '<div class="co-policy__group-divider ' + variantClass + '">';
        html += '<p class="co-policy__group-divider-kicker">' + gb.kicker + '</p>';
        html += '<div class="co-policy__group-divider-title">' + gb.title + '</div>';
        html += '<p class="co-policy__group-divider-subtitle">' + gb.subtitle + '</p>';
        html += '</div>';
      }

      var secId = 'policy-section-' + (j + 1);
      html += '<div class="co-policy__section" id="' + secId + '">';
      html += '<div class="co-policy__section-header">';
      html += '<span class="co-policy__section-num">' + (j + 1) + '</span>';
      html += '<h2 class="co-policy__section-title">' + sections[j].title + '</h2>';
      html += '</div>';
      html += sections[j].content;
      html += '</div>';
    }
    html += '</div>'; // .co-policy__content
    html += '</div>'; // .co-policy__layout
    html += '</div>'; // .co-policy

    // Back to top button
    html += '<button class="co-policy__back-top" aria-label="Back to top" title="Back to top">';
    html += '<i class="ri-arrow-up-line"></i>';
    html += '</button>';

    // Replace the container content
    container.innerHTML = html;

    // Remove the old section styling so our card layout shows cleanly
    var section = container.closest('section.text-dark.static');
    if (section) {
      section.style.background = 'transparent';
      section.style.padding = '0';
    }
    container.style.maxWidth = 'none';
    container.style.padding = '0';
  }

  /**
   * TOC active state: highlight the current section in view
   */
  function setupTocHighlight() {
    var tocLinks = document.querySelectorAll('.co-policy__toc-list a');
    if (tocLinks.length === 0) return;

    var sections = [];
    tocLinks.forEach(function (link) {
      var id = link.getAttribute('href');
      if (id && id.charAt(0) === '#') {
        var el = document.getElementById(id.substring(1));
        if (el) sections.push({ link: link, el: el });
      }
    });

    if (sections.length === 0) return;

    var ticking = false;
    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(function () {
          var current = null;

          for (var i = sections.length - 1; i >= 0; i--) {
            if (sections[i].el.getBoundingClientRect().top <= 80) {
              current = sections[i];
              break;
            }
          }

          tocLinks.forEach(function (l) {
            l.classList.remove('co-policy__toc-active');
          });
          if (current) {
            current.link.classList.add('co-policy__toc-active');
          }

          ticking = false;
        });
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /**
   * Back to top button: show/hide on scroll
   */
  function setupBackToTop() {
    var btn = document.querySelector('.co-policy__back-top');
    if (!btn) return;

    var ticking = false;
    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(function () {
          var scrollTop = window.scrollY || document.documentElement.scrollTop;
          if (scrollTop > 400) {
            btn.classList.add('co-policy__back-top--visible');
          } else {
            btn.classList.remove('co-policy__back-top--visible');
          }
          ticking = false;
        });
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  /**
   * Smooth scroll for TOC anchor links
   */
  function setupSmoothScroll() {
    document.querySelectorAll('.co-policy__toc-list a').forEach(function (link) {
      link.addEventListener('click', function (e) {
        var href = this.getAttribute('href');
        if (href && href.charAt(0) === '#') {
          var target = document.getElementById(href.substring(1));
          if (target) {
            e.preventDefault();
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            history.pushState(null, '', href);
          }
        }
      });
    });
  }

})();
